import { createContext, useContext, useRef, type RefObject } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { ignorandoElPanel } from '@/lib/softphone/panel';
import { cn } from '@/lib/utils';
import './animations.css';

/** Quién tenía el foco cuando este diálogo se abrió. Lo lee `DialogContent`. */
const QuienAbrio = createContext<RefObject<HTMLElement | null> | null>(null);

/**
 * Nuestro `Dialog` en vez del de Radix por una sola razón: anotar quién lo abre.
 *
 * Radix devuelve el foco al `DialogTrigger` al cerrar, y en esta app **no hay
 * ninguno**: los 36 diálogos son controlados con `open` desde afuera y los abre
 * un botón cualquiera de la pantalla. Con `triggerRef` vacío Radix igual cancela
 * la restauración que haría el `FocusScope`, así que el foco terminaba en
 * `<body>` en cada cierre y quien navega con el teclado volvía al principio del
 * documento después de cada «Cancelar».
 *
 * Y no sirve `onOpenAutoFocus` para anotarlo: un diálogo con un campo `autoFocus`
 * —Equipos, Etiquetas— ya tiene el foco adentro cuando el `FocusScope` se monta,
 * y entonces ese evento ni se dispara. El render del cambio sí es un momento
 * fiable: todavía no hubo commit, así que el foco sigue donde lo dejó el clic.
 */
export function Dialog({ open, ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const quienAbrio = useRef<HTMLElement | null>(null);
  const abiertoAntes = useRef(false);

  if (open === true && !abiertoAntes.current) {
    const activo = document.activeElement;
    quienAbrio.current = activo instanceof HTMLElement && activo !== document.body ? activo : null;
  }
  abiertoAntes.current = open === true;

  return (
    <QuienAbrio.Provider value={quienAbrio}>
      <DialogPrimitive.Root open={open} {...props} />
    </QuienAbrio.Provider>
  );
}

export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

/**
 * The scrim has to read as near-black in both themes, and `--foreground` /
 * `--background` are the two tokens that swap places when the theme flips.
 */
export const OVERLAY_SCRIM = 'bg-foreground/20 dark:bg-background/70';

export type DialogContentProps = React.ComponentProps<typeof DialogPrimitive.Content> & {
  showClose?: boolean;
};

export function DialogContent({
  className,
  children,
  showClose = true,
  onCloseAutoFocus,
  onInteractOutside,
  onPointerDownOutside,
  onFocusOutside,
  ...props
}: DialogContentProps) {
  const quienAbrio = useContext(QuienAbrio);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className={cn('ui-anim-overlay fixed inset-0 z-50', OVERLAY_SCRIM)} />
      {/* Centring lives on the wrapper so the content's transform stays free for the animation. */}
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <DialogPrimitive.Content
          className={cn(
            'ui-anim-dialog pointer-events-auto relative flex w-full max-w-lg flex-col gap-4 rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg',
            className,
          )}
          // El foco vuelve a quien abrió el diálogo. Quien pase el suyo manda
          // —`capture-gallery` lo devuelve a su propia miniatura—: sólo si no
          // decidió nada lo devolvemos nosotros.
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            const destino = quienAbrio?.current ?? null;
            if (destino === null || !destino.isConnected) return;
            event.preventDefault();
            destino.focus();
          }}
          onInteractOutside={ignorandoElPanel(onInteractOutside)}
          onPointerDownOutside={ignorandoElPanel(onPointerDownOutside)}
          onFocusOutside={ignorandoElPanel(onFocusOutside)}
          {...props}
        >
          {children}
          {showClose ? (
            <DialogPrimitive.Close
              aria-label="Cerrar"
              className="absolute right-3 top-3 inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground"
            >
              <X className="size-4" />
            </DialogPrimitive.Close>
          ) : null}
        </DialogPrimitive.Content>
      </div>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1 pr-8', className)} {...props} />;
}

export function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      className={cn('text-base font-medium leading-none tracking-[-0.01em]', className)}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}
